import { useEffect, useMemo } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { LayoutDashboard, FolderKanban, Languages, MessageSquare, LogOut, Globe, Layers, HelpCircle, Users, FileText, Handshake, Settings, Ruler, SearchCheck } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const LOGO_BLUE = `${process.env.REACT_APP_BACKEND_URL}/api/uploads/logo_transparent.png`;

const isTokenExpired = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp * 1000 < Date.now();
  } catch {
    return true;
  }
};

const clearSession = () => {
  localStorage.removeItem('itcs-admin-token');
  localStorage.removeItem('itcs-admin-user');
};

export default function AdminLayout() {
  const location = useLocation();
  const navigate = useNavigate();
  const token = localStorage.getItem('itcs-admin-token');

  useEffect(() => {
    if (!token) {
      navigate('/admin/login');
      return;
    }
    if (isTokenExpired(token)) {
      clearSession();
      toast.error('Sitzung abgelaufen – bitte neu anmelden', { id: 'session-expired' });
      navigate('/admin/login');
    }
  }, [token, navigate]);

  useEffect(() => {
    const id = axios.interceptors.response.use(
      (res) => res,
      (error) => {
        if (error.response?.status === 401) {
          clearSession();
          toast.error('Sitzung abgelaufen – bitte neu anmelden', { id: 'session-expired' });
          navigate('/admin/login');
        }
        return Promise.reject(error);
      }
    );
    return () => axios.interceptors.response.eject(id);
  }, [navigate]);

  const menuItems = useMemo(() => [
    { to: '/admin', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { to: '/admin/projects', label: 'Projekte', icon: FolderKanban },
    { to: '/admin/services', label: 'Leistungen', icon: Layers },
    { to: '/admin/seo', label: 'SEO', icon: SearchCheck },
    { to: '/admin/faq', label: 'FAQ', icon: HelpCircle },
    { to: '/admin/team', label: 'Team', icon: Users },
    { to: '/admin/partners', label: 'Partner', icon: Handshake },
    { to: '/admin/planner', label: 'Hallenplaner', icon: Ruler },
    { to: '/admin/blog', label: 'Blog / News', icon: FileText },
    { to: '/admin/contacts', label: 'Anfragen', icon: MessageSquare },
    { to: '/admin/translations', label: 'Übersetzungen', icon: Languages },
    { to: '/admin/settings', label: 'Einstellungen', icon: Settings },
  ], []);

  const isActive = (item) => item.exact ? location.pathname === item.to : location.pathname.startsWith(item.to);

  const handleLogout = () => {
    localStorage.removeItem('itcs-admin-token');
    localStorage.removeItem('itcs-admin-user');
    navigate('/admin/login');
  };

  if (!token) return null;

  return (
    <div className="flex h-screen bg-slate-50" data-testid="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar w-64 shrink-0 flex flex-col" data-testid="admin-sidebar">
        <div className="p-5 border-b border-slate-800">
          <img src={LOGO_BLUE} alt="ITCS" className="h-10" />
          <p className="text-xs text-slate-500 mt-1">Admin Panel</p>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          {menuItems.map(item => (
            <Link
              key={item.to}
              to={item.to}
              data-testid={`admin-nav-${item.label.toLowerCase()}`}
              className={`admin-sidebar-link ${isActive(item) ? 'active' : ''}`}
            >
              <item.icon className="w-5 h-5" />
              {item.label}
            </Link>
          ))}
        </nav>
        <div className="p-4 border-t border-slate-800 space-y-2">
          <Link to="/" className="admin-sidebar-link">
            <Globe className="w-5 h-5" /> Website
          </Link>
          <button onClick={handleLogout} className="admin-sidebar-link w-full text-red-400 hover:text-red-300 hover:bg-red-500/10" data-testid="admin-logout-btn">
            <LogOut className="w-5 h-5" /> Abmelden
          </button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 overflow-y-auto">
        <header className="bg-white border-b border-slate-200 px-8 py-4 flex items-center justify-between sticky top-0 z-30">
          <h2 className="text-lg font-bold text-[#0F172A]">ITCS Admin</h2>
          <span className="text-sm text-slate-500">{localStorage.getItem('itcs-admin-user') || 'Admin'}</span>
        </header>
        <div className="p-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
